import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MultiSelect } from '../ui/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar } from '../ui/calendar';
import { FileText, Download, Trash2, Search, X, Loader2, CalendarDays, RefreshCw, CheckCircle2, FileEdit, KeyRound, Wrench, CalendarClock, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { ZohoAuthModal } from './ZohoAuthModal';
import { BookingRepairModal } from './BookingRepairModal';
import { BookingDetailsModal } from './BookingDetailsModal';
import { RescheduleBookingModal } from '../bookings/RescheduleBookingModal';

const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'custom', label: 'Custom Range' },
];

const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'pending', label: 'Pending' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Display labels for each bookable_type — the Space filter groups by this
// category instead of listing every individual room/desk.
const SPACE_TYPE_LABELS = {
  meeting_room: 'Meeting Room',
  conference_room: 'Conference Room',
  day_pass: 'Day Pass',
  dedicated_desk: 'Dedicated Desk',
  event_space: 'Event Space',
  virtual_office: 'Virtual Office',
};

export const BookingsTab = ({ bookings, venues, bookables, users, user, api, theme, onRefresh }) => {
  const { cardBg, cardBorder, hoverBg } = theme;
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [couponFilter, setCouponFilter] = useState([]);
  const [venueFilter, setVenueFilter] = useState([]);
  const [spaceFilter, setSpaceFilter] = useState([]);
  // Column sort: 'date' sorts by booking_date, 'time' by start_time — click
  // the same header again to flip direction, defaults to newest/earliest first.
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [dateFilter, setDateFilter] = useState('all');
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [showStartCal, setShowStartCal] = useState(false);
  const [showEndCal, setShowEndCal] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [recordingPaid, setRecordingPaid] = useState(false);
  const [regenBooking, setRegenBooking] = useState(null);
  const [showZohoAuth, setShowZohoAuth] = useState(false);
  const [repairBooking, setRepairBooking] = useState(null);
  const [rescheduleBooking, setRescheduleBooking] = useState(null);
  const [detailsBooking, setDetailsBooking] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const reconcilePending = async () => {
    if (!window.confirm('Poll Razorpay for every booking stuck on Pending Payment and mark the ones already paid as Confirmed. Continue?')) return;
    setReconciling(true);
    try {
      const res = await api.post('/virtual-office/admin/reconcile-pending-bookings');
      const { checked, confirmed, still_unpaid, errors } = res.data;
      const msg = `Checked ${checked} · Confirmed ${confirmed} · Still unpaid ${still_unpaid}${errors?.length ? ` · ${errors.length} errors` : ''}`;
      if (confirmed > 0) toast.success(msg);
      else toast.info(msg);
      if (errors?.length) console.warn('[BOOKING-RECONCILE]', errors);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setReconciling(false);
    }
  };

  const markPaidInZoho = async () => {
    if (!window.confirm('Record the Razorpay payment in Zoho for every paid booking + VO subscription so Outstanding invoices flip to Paid. Continue?')) return;
    setRecordingPaid(true);
    try {
      const res = await api.post('/virtual-office/admin/record-paid-invoices');
      const { vo, bookings: bk } = res.data;
      const msg = `Bookings: ${bk.recorded}/${bk.total} recorded · VO: ${vo.recorded}/${vo.total} recorded`;
      if ((bk.failed || 0) + (vo.failed || 0) > 0) toast.warning(msg);
      else toast.success(msg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setRecordingPaid(false);
    }
  };

  const getVenueName = (venueId) => venues?.find(v => v.id === venueId)?.name || '-';
  const getBookableName = (bookableId) => bookables?.find(b => b.id === bookableId)?.name || '-';
  const getBookableCategory = (booking) => bookables?.find(bk => bk.id === booking.bookable_id)?.bookable_type || null;
  const getUserName = (userId) => users?.find(u => u.id === userId)?.name || '';
  const getUserEmail = (userId) => users?.find(u => u.id === userId)?.email || '';

  // guest → registered user → organization. CM "Book for Client" bookings can
  // target an org without a member, so they carry only organization_id; the
  // API resolves user_name / organization_name onto every booking.
  const getGuestDisplay = (booking) => {
    if (booking.guest_name) return { name: booking.guest_name, email: booking.guest_email || '' };
    const name = booking.user_name || getUserName(booking.user_id) || booking.organization_name;
    const email = booking.user_email || getUserEmail(booking.user_id);
    if (name) return { name, email };
    return { name: 'N/A', email: '' };
  };

  const getBookingType = (booking) => {
    if (booking.notes?.startsWith('Walk-in:') || (booking.guest_name && !booking.is_internal_client && booking.created_by_role !== 'sales')) return 'Walk-in';
    if (booking.is_internal_client) return 'Booked by Client';
    if (booking.created_by_role === 'sales') return 'Sales Booking';
    if (booking.is_internal || booking.notes?.startsWith('Internal:')) return 'Internal';
    return 'Booking';
  };

  const getBookingTypeBadgeColor = (type) => {
    switch (type) {
      case 'Walk-in': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'Booked by Client': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      case 'Sales Booking': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400';
      case 'Internal': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
      case 'completed': return 'bg-primary/20 text-primary border-primary/30';
      case 'pending': return 'bg-amber-500/20 text-amber-500 border-amber-500/30';
      case 'pending_payment': return 'bg-yellow-500/20 text-yellow-600 border-yellow-500/30';
      case 'cancelled': return 'bg-red-500/20 text-red-500 border-red-500/30';
      default: return 'bg-secondary text-secondary-foreground';
    }
  };

  const getDateRange = () => {
    const now = new Date();
    switch (dateFilter) {
      case 'today': return { start: startOfDay(now), end: endOfDay(now) };
      case 'week': return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month': return { start: startOfMonth(now), end: endOfMonth(now) };
      case 'custom': return { start: customStart ? startOfDay(customStart) : null, end: customEnd ? endOfDay(customEnd) : null };
      default: return null;
    }
  };

  // Coupon usage summary — distinct codes found across all loaded bookings,
  // each with a count, powering both the admin filter dropdown and the
  // "times used" figure requested for coupon tracking/reporting.
  const couponStats = useMemo(() => {
    const counts = new Map();
    bookings.forEach(b => {
      if (!b.coupon_code) return;
      counts.set(b.coupon_code, (counts.get(b.coupon_code) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count);
  }, [bookings]);
  const isAdmin = user?.role === 'admin';

  const couponOptions = useMemo(() => ([
    { value: 'has_coupon', label: 'Any Coupon' },
    ...couponStats.map(c => ({ value: c.code, label: c.code })),
  ]), [couponStats]);

  // Venue/space filter option lists, derived from the bookings actually
  // loaded so every option is guaranteed to match at least one row.
  const venueOptions = useMemo(() => {
    const map = new Map();
    bookings.forEach(b => {
      if (!b.venue_id) return;
      const label = b.venue_name || getVenueName(b.venue_id);
      if (!map.has(b.venue_id)) map.set(b.venue_id, { value: b.venue_id, label, count: 0 });
      map.get(b.venue_id).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [bookings]);

  // Space options group by category (Meeting Room, Day Pass, ...) rather
  // than listing every individual room, and narrow to the currently
  // selected venue(s) so the list never offers a category with 0 results.
  const spaceOptions = useMemo(() => {
    const map = new Map();
    bookings.forEach(b => {
      if (venueFilter.length > 0 && !venueFilter.includes(b.venue_id)) return;
      const category = getBookableCategory(b);
      if (!category) return;
      if (!map.has(category)) map.set(category, { value: category, label: SPACE_TYPE_LABELS[category] || category, count: 0 });
      map.get(category).count += 1;
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [bookings, bookables, venueFilter]);

  // Drop any selected space that fell out of scope after the venue filter changed.
  useEffect(() => {
    setSpaceFilter(prev => {
      if (prev.length === 0) return prev;
      const validIds = new Set(spaceOptions.map(o => o.value));
      const pruned = prev.filter(id => validIds.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [spaceOptions]);

  const filteredBookings = useMemo(() => {
    let result = bookings;
    // Date filter
    const range = getDateRange();
    if (range) {
      result = result.filter(b => {
        if (!b.booking_date) return false;
        const bd = parseISO(b.booking_date);
        if (range.start && bd < range.start) return false;
        if (range.end && bd > range.end) return false;
        return true;
      });
    }
    // Venue filter (multi-select)
    if (venueFilter.length > 0) {
      result = result.filter(b => venueFilter.includes(b.venue_id));
    }
    // Space filter (multi-select, by category)
    if (spaceFilter.length > 0) {
      result = result.filter(b => spaceFilter.includes(getBookableCategory(b)));
    }
    // Status filter (multi-select)
    if (statusFilter.length > 0) {
      result = result.filter(b => statusFilter.includes(b.status));
    }
    // Coupon filter (admin only, multi-select)
    if (isAdmin && couponFilter.length > 0) {
      result = couponFilter.includes('has_coupon')
        ? result.filter(b => !!b.coupon_code)
        : result.filter(b => couponFilter.includes(b.coupon_code));
    }
    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(b => {
        const guest = getGuestDisplay(b);
        return (
          b.id?.toLowerCase().includes(q) ||
          b.booking_date?.includes(q) ||
          getVenueName(b.venue_id)?.toLowerCase().includes(q) ||
          getBookableName(b.bookable_id)?.toLowerCase().includes(q) ||
          guest.name?.toLowerCase().includes(q) ||
          guest.email?.toLowerCase().includes(q) ||
          b.status?.toLowerCase().includes(q)
        );
      });
    }
    // Sort by the active column (defaults to booking_date descending, i.e.
    // most recent first) while a date-range filter still shows the full
    // chronological span within that range.
    const sortKey = sortField === 'time' ? 'start_time' : 'booking_date';
    result = [...result].sort((a, b) => {
      const cmp = (a[sortKey] || '').localeCompare(b[sortKey] || '');
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [bookings, bookables, searchQuery, statusFilter, couponFilter, venueFilter, spaceFilter, isAdmin, dateFilter, customStart, customEnd, sortField, sortDirection]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection(field === 'time' ? 'asc' : 'desc');
    }
  };

  // Reset to page 1 whenever the filtered set changes underneath the pager,
  // otherwise a filter change can strand the view on a now-empty page.
  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, couponFilter, venueFilter, spaceFilter, dateFilter, customStart, customEnd, pageSize]);

  const hasActiveFilters = !!searchQuery || statusFilter.length > 0 || couponFilter.length > 0 ||
    venueFilter.length > 0 || spaceFilter.length > 0 || dateFilter !== 'all';

  const clearAllFilters = () => {
    setSearchQuery('');
    setStatusFilter([]);
    setCouponFilter([]);
    setVenueFilter([]);
    setSpaceFilter([]);
    setDateFilter('all');
    setCustomStart(null);
    setCustomEnd(null);
  };

  const totalPages = Math.max(1, Math.ceil(filteredBookings.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedBookings = filteredBookings.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Builds a compact page-number list with the current page anchored and
  // "…" gaps elsewhere, e.g. [1, '…', 4, 5, 6, '…', 20] for large page counts.
  const getPageNumbers = (current, total) => {
    const delta = 2;
    const pages = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        pages.push(i);
      }
    }
    const withGaps = [];
    let prev;
    pages.forEach(p => {
      if (prev && p - prev > 1) withGaps.push('...');
      withGaps.push(p);
      prev = p;
    });
    return withGaps;
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;
    setCancellingId(bookingId);
    try {
      await api.post(`/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      onRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancellingId(null);
    }
  };

  const handleDownloadInvoice = async (bookingId) => {
    setDownloadingId(bookingId);
    try {
      const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Clayworks_Invoice_${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      try {
        await api.get(`/bookings/${bookingId}/invoice`);
        const res = await api.get(`/bookings/${bookingId}/invoice/pdf`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `Clayworks_Invoice_${bookingId.slice(0, 8)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Invoice generated and downloaded');
      } catch (e) {
        toast.error('Failed to download invoice');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadExport = async (fmt) => {
    try {
      const endpoint = fmt === 'csv' ? '/bookings/export/csv' : '/bookings/export/pdf';
      const response = await api.get(endpoint, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: fmt === 'csv' ? 'text/csv' : 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings_${fmt}_${new Date().toISOString().slice(0, 10)}.${fmt === 'csv' ? 'csv' : 'html'}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded successfully`);
    } catch {
      toast.error(`Failed to download ${fmt.toUpperCase()}`);
    }
  };

  return (
    <Card className={`${cardBg} ${cardBorder}`} data-testid="bookings-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">All Bookings</CardTitle>
        {(user?.role === 'admin' || user?.role === 'hub_manager') && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={reconcilePending}
              disabled={reconciling}
              className="border-blue-500/50 text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/30"
              data-testid="reconcile-pending-bookings-btn"
              title="Poll Razorpay for every Pending Payment booking and mark the ones already paid as Confirmed. Fixes bookings stuck pending after a client paid but didn't return to the app."
            >
              {reconciling ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Reconcile Pending
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={markPaidInZoho}
              disabled={recordingPaid}
              className="border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
              data-testid="mark-paid-zoho-btn"
              title="Record the Razorpay payment in Zoho for every paid booking so its invoice flips Outstanding → Paid."
            >
              {recordingPaid ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Mark Paid in Zoho
            </Button>
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowZohoAuth(true)}
                className="border-purple-500/50 text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-950/30"
                data-testid="zoho-auth-btn"
                title="Regenerate Zoho OAuth refresh token — required after Zoho rejects payment recording with 'not authorized'."
              >
                <KeyRound className="w-4 h-4 mr-1.5" /> Zoho Auth
              </Button>
            )}
            <Button variant="outline" size="sm" className={cardBorder} onClick={() => downloadExport('csv')} data-testid="download-csv-btn">
              <FileText className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button variant="outline" size="sm" className={cardBorder} onClick={() => downloadExport('pdf')} data-testid="download-pdf-btn">
              <FileText className="w-4 h-4 mr-2" /> PDF
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Date Period Filter */}
        <div className="flex flex-wrap gap-2 mb-3" data-testid="date-period-filter">
          {DATE_PRESETS.map(p => (
            <Button
              key={p.value}
              size="sm"
              variant={dateFilter === p.value ? 'default' : 'outline'}
              className={dateFilter === p.value ? '' : cardBorder}
              onClick={() => { setDateFilter(p.value); if (p.value !== 'custom') { setCustomStart(null); setCustomEnd(null); } }}
              data-testid={`date-filter-${p.value}`}
            >
              {p.value === 'custom' && <CalendarDays className="w-3.5 h-3.5 mr-1.5" />}
              {p.label}
            </Button>
          ))}
        </div>

        {/* Custom Date Range Pickers */}
        {dateFilter === 'custom' && (
          <div className="flex flex-wrap items-center gap-3 mb-3 p-3 rounded-lg border border-dashed" data-testid="custom-date-range">
            <Popover open={showStartCal} onOpenChange={setShowStartCal}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[140px] justify-start" data-testid="custom-start-date">
                  <CalendarDays className="w-3.5 h-3.5 mr-2" />
                  {customStart ? format(customStart, 'MMM dd, yyyy') : 'Start Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={customStart} onSelect={(d) => { setCustomStart(d); setShowStartCal(false); }} />
              </PopoverContent>
            </Popover>
            <span className="text-muted-foreground text-sm">to</span>
            <Popover open={showEndCal} onOpenChange={setShowEndCal}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[140px] justify-start" data-testid="custom-end-date">
                  <CalendarDays className="w-3.5 h-3.5 mr-2" />
                  {customEnd ? format(customEnd, 'MMM dd, yyyy') : 'End Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={customEnd} onSelect={(d) => { setCustomEnd(d); setShowEndCal(false); }} />
              </PopoverContent>
            </Popover>
            {(customStart || customEnd) && (
              <Button variant="ghost" size="sm" onClick={() => { setCustomStart(null); setCustomEnd(null); }} className="text-muted-foreground">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            )}
          </div>
        )}

        {/* Search & Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
          <div className="relative w-full lg:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by ID, venue, space, guest, date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-8"
              data-testid="bookings-search-input"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
            <MultiSelect
              options={venueOptions}
              selected={venueFilter}
              onChange={setVenueFilter}
              placeholder="Venue"
              allLabel="All Venues"
              triggerClassName="w-[160px]"
              searchable={false}
              data-testid="bookings-venue-filter"
            />
            <MultiSelect
              options={spaceOptions}
              selected={spaceFilter}
              onChange={setSpaceFilter}
              placeholder="Space Type"
              allLabel="All Spaces"
              triggerClassName="w-[160px]"
              searchable={false}
              data-testid="bookings-space-filter"
            />
            <MultiSelect
              options={STATUS_OPTIONS}
              selected={statusFilter}
              onChange={setStatusFilter}
              placeholder="Status"
              allLabel="All Status"
              triggerClassName="w-[160px]"
              searchable={false}
              data-testid="bookings-status-filter"
            />
            {isAdmin && (
              <MultiSelect
                options={couponOptions}
                selected={couponFilter}
                onChange={setCouponFilter}
                placeholder="Coupon"
                allLabel="All Bookings"
                triggerClassName="w-[160px]"
                searchable={false}
                data-testid="bookings-coupon-filter"
              />
            )}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-9 text-muted-foreground shrink-0"
                data-testid="bookings-clear-filters"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Clear Filters
              </Button>
            )}
          </div>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          {filteredBookings.length === 0
            ? `0 of ${bookings.length} bookings`
            : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredBookings.length)} of ${filteredBookings.length} bookings (${bookings.length} total)`}
          {isAdmin && couponFilter.length > 0 && !couponFilter.includes('has_coupon') && (
            <span className="ml-1">
              · {couponFilter.map(code => `"${code}" used ${couponStats.find(c => c.code === code)?.count || 0}x`).join(', ')}
            </span>
          )}
        </p>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={`border-b ${cardBorder}`}>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">ID</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => toggleSort('date')}
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    data-testid="bookings-sort-date"
                  >
                    Date
                    {sortField === 'date'
                      ? (sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
                      : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                  </button>
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => toggleSort('time')}
                    className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
                    data-testid="bookings-sort-time"
                  >
                    Time
                    {sortField === 'time'
                      ? (sortDirection === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
                      : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
                  </button>
                </th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Venue</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Space</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Guest</th>
                <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Type</th>
                <th className="text-right py-3 px-2 text-sm font-medium text-muted-foreground">Amount</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Payment</th>
                {isAdmin && <th className="text-left py-3 px-2 text-sm font-medium text-muted-foreground">Coupon</th>}
                <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Status</th>
                <th className="text-center py-3 px-2 text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings.length === 0 ? (
                <tr><td colSpan={isAdmin ? 11 : 10} className="py-8 text-center text-muted-foreground">{hasActiveFilters ? 'No bookings match your filters' : 'No bookings yet'}</td></tr>
              ) : pagedBookings.map(booking => {
                const guest = getGuestDisplay(booking);
                const bookingType = getBookingType(booking);
                return (
                  <tr
                    key={booking.id}
                    className={`border-b ${cardBorder}/50 ${hoverBg} transition-colors cursor-pointer`}
                    onClick={() => {
                      // Enrich the booking with human-readable names resolved from
                      // the parent tab's users / venues / bookables arrays so the
                      // details modal never renders bare UUIDs for legacy rows.
                      const enriched = { ...booking };
                      const venue = venues?.find(v => v.id === booking.venue_id);
                      const bookable = bookables?.find(bk => bk.id === booking.bookable_id);
                      const bookedUser = users?.find(u => u.id === booking.user_id);
                      const creatorUser = users?.find(u => u.id === booking.created_by);
                      const updaterUser = booking.updated_by ? users?.find(u => u.id === booking.updated_by) : null;
                      if (!enriched.venue_name) enriched.venue_name = venue?.name || null;
                      if (!enriched.region) enriched.region = venue?.region || venue?.city || null;
                      if (!enriched.bookable_name) enriched.bookable_name = bookable?.name || null;
                      if (!enriched.user_name && bookedUser) enriched.user_name = bookedUser.name;
                      if (!enriched.user_email && bookedUser) enriched.user_email = bookedUser.email;
                      if (!enriched.organization_name && bookedUser) enriched.organization_name = bookedUser.organization_name || null;
                      if (!enriched.created_by_name && creatorUser) enriched.created_by_name = creatorUser.name;
                      if (!enriched.created_by_role && creatorUser) enriched.created_by_role = creatorUser.role;
                      if (!enriched.updated_by_name && updaterUser) enriched.updated_by_name = updaterUser.name;
                      if (!enriched.updated_by_role && updaterUser) enriched.updated_by_role = updaterUser.role;
                      if (!enriched.booking_type) enriched.booking_type = getBookingType(booking);
                      setDetailsBooking(enriched);
                    }}
                    data-testid={`admin-booking-row-${booking.id?.slice(0, 8)}`}
                    title="Click to view full booking details"
                  >
                    <td className="py-3 px-2 text-sm font-mono">#{booking.id?.slice(0, 8)}</td>
                    <td className="py-3 px-2 text-sm">{booking.booking_date}</td>
                    <td className="py-3 px-2 text-sm text-muted-foreground">{booking.start_time} - {booking.end_time}</td>
                    <td className="py-3 px-2 text-sm">{booking.venue_name || getVenueName(booking.venue_id)}</td>
                    <td className="py-3 px-2 text-sm">{booking.bookable_name || getBookableName(booking.bookable_id)}</td>
                    <td className="py-3 px-2">
                      <div className="text-sm font-medium" data-testid={`admin-booking-guest-${booking.id?.slice(0, 8)}`}>{guest.name}</div>
                      {guest.email && <div className="text-xs text-muted-foreground">{guest.email}</div>}
                    </td>
                    <td className="py-3 px-2">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${getBookingTypeBadgeColor(bookingType)}`}>
                        {bookingType}
                      </span>
                    </td>
                    <td className="py-3 px-2 text-sm text-right font-heading font-semibold">₹{booking.total_amount?.toLocaleString()}</td>
                    <td className="py-3 px-2 text-center">
                      {(() => {
                        const pm = booking.is_internal
                          ? { label: 'Internal', cls: 'bg-slate-500/15 text-slate-600 border-slate-500/30' }
                          : (booking.credits_used || 0) > 0 && (booking.total_amount || 0) === 0
                          ? { label: 'Credits', cls: 'bg-amber-500/15 text-amber-700 border-amber-500/30' }
                          : (booking.payment_type && booking.payment_type.includes('razorpay')) || booking.razorpay_payment_link_id || booking.razorpay_payment_id
                          ? { label: 'Razorpay', cls: 'bg-blue-500/15 text-blue-700 border-blue-500/30' }
                          : booking.payment_type === 'cash'
                          ? { label: 'Cash', cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' }
                          : booking.payment_type === 'manual' || booking.payment_type === 'bank_transfer'
                          ? { label: booking.payment_type === 'bank_transfer' ? 'Bank' : 'Manual', cls: 'bg-purple-500/15 text-purple-700 border-purple-500/30' }
                          : (booking.total_amount || 0) === 0
                          ? { label: 'Free', cls: 'bg-gray-400/15 text-gray-600 border-gray-400/30' }
                          : { label: 'Unrecorded', cls: 'bg-red-400/15 text-red-600 border-red-400/30' };
                        return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${pm.cls}`} data-testid={`admin-booking-payment-${booking.id?.slice(0, 8)}`}>{pm.label}</span>;
                      })()}
                    </td>
                    {isAdmin && (
                      <td className="py-3 px-2 text-sm" data-testid={`admin-booking-coupon-${booking.id?.slice(0, 8)}`}>
                        {booking.coupon_code
                          ? <Badge variant="outline" className="font-mono text-xs">{booking.coupon_code}</Badge>
                          : <span className="text-muted-foreground">-</span>}
                      </td>
                    )}
                    <td className="py-3 px-2 text-center">
                      <Badge className={`${getStatusColor(booking.status)} border`}>{booking.status?.replace('_', ' ')}</Badge>
                    </td>
                    <td className="py-3 px-2 text-center" onClick={(e) => e.stopPropagation()}>
                      {booking.status !== 'cancelled' && (
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setRepairBooking(booking)}
                            className="text-slate-500 hover:text-slate-700"
                            title="Diagnose & repair — inspect + fix missing created_by / payment_type / total_amount drift"
                            data-testid={`admin-diagnose-${booking.id?.slice(0, 8)}`}
                          >
                            <Wrench className="w-3.5 h-3.5" />
                          </Button>
                          {['pending', 'pending_payment', 'confirmed'].includes(booking.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRescheduleBooking(booking)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Reschedule — no charge, client is notified by email"
                              data-testid={`admin-reschedule-${booking.id?.slice(0, 8)}`}
                            >
                              <CalendarClock className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {(booking.total_amount || 0) > 0 && (booking.credits_used || 0) === 0 && !booking.is_internal && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDownloadInvoice(booking.id)}
                              className="text-primary hover:text-primary/80"
                              title="Download Invoice"
                              disabled={downloadingId === booking.id}
                              data-testid={`admin-invoice-${booking.id?.slice(0, 8)}`}
                            >
                              {downloadingId === booking.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                            </Button>
                          )}
                          {booking.zoho_invoice_id && !booking.zoho_payment_recorded && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={async () => {
                                if (!window.confirm('Force-record this booking\'s payment in Zoho? Diagnostic info will be shown if it fails.')) return;
                                try {
                                  const res = await api.post(`/virtual-office/admin/force-mark-paid/${booking.id}`);
                                  if (res.data.final === 'success') {
                                    toast.success(`Paid: ${res.data.zoho_invoice_number} (Balance: ₹${res.data.zoho_balance_after || 0})`);
                                    onRefresh && onRefresh();
                                  } else if (res.data.final === 'already_paid') {
                                    toast.info('Zoho says this invoice is already Paid');
                                    onRefresh && onRefresh();
                                  } else {
                                    console.error('[FORCE-MARK-PAID diagnostics]', res.data);
                                    // Find the failing step and extract Zoho's error message
                                    const failStep = (res.data.steps || []).reverse().find(s => s.status === 'FAIL');
                                    const zohoMsg = failStep?.detail?.zoho_message
                                      || failStep?.detail?.reason
                                      || failStep?.reason
                                      || res.data.final;
                                    toast.error(`Zoho rejected: ${zohoMsg}`, { duration: 10000 });
                                  }
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || 'Force-mark failed');
                                }
                              }}
                              className="text-emerald-600 hover:text-emerald-700"
                              title="Force-record this booking's payment in Zoho. Use when the bulk 'Mark Paid in Zoho' skipped it for some reason."
                              data-testid={`admin-force-paid-${booking.id?.slice(0, 8)}`}
                            >
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {booking.zoho_invoice_id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setRegenBooking(booking)}
                              className="text-blue-600 hover:text-blue-700"
                              title="Fix Invoice — regenerate with corrected company name + GSTIN"
                              data-testid={`admin-regen-${booking.id?.slice(0, 8)}`}
                            >
                              <FileEdit className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleCancelBooking(booking.id)}
                            className="text-red-500 hover:text-red-600"
                            disabled={cancellingId === booking.id}
                            title="Cancel Booking"
                            data-testid={`admin-cancel-${booking.id?.slice(0, 8)}`}
                          >
                            {cancellingId === booking.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="bookings-pagination">
            <div className="flex items-center gap-1 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                className={cardBorder}
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                data-testid="bookings-prev-page"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
              </Button>
              {getPageNumbers(currentPage, totalPages).map((p, idx) =>
                p === '...' ? (
                  <span key={`gap-${idx}`} className="px-1.5 text-sm text-muted-foreground">...</span>
                ) : (
                  <Button
                    key={p}
                    variant={p === currentPage ? 'default' : 'outline'}
                    size="sm"
                    className={`w-8 h-8 p-0 ${p === currentPage ? '' : cardBorder}`}
                    onClick={() => setPage(p)}
                    data-testid={`bookings-page-${p}`}
                  >
                    {p}
                  </Button>
                )
              )}
              <Button
                variant="outline"
                size="sm"
                className={cardBorder}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                data-testid="bookings-next-page"
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="w-[130px]" data-testid="bookings-page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 per page</SelectItem>
                <SelectItem value="50">50 per page</SelectItem>
                <SelectItem value="100">100 per page</SelectItem>
                <SelectItem value="200">200 per page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </CardContent>
      <RegenerateBookingInvoiceDialog
        open={!!regenBooking}
        onClose={() => setRegenBooking(null)}
        booking={regenBooking}
        api={api}
        onDone={onRefresh}
      />
      <ZohoAuthModal
        open={showZohoAuth}
        onClose={() => setShowZohoAuth(false)}
        api={api}
      />
      <BookingRepairModal
        open={!!repairBooking}
        onClose={() => setRepairBooking(null)}
        bookingId={repairBooking?.id}
        api={api}
        onDone={onRefresh}
      />
      <RescheduleBookingModal
        open={!!rescheduleBooking}
        onClose={() => setRescheduleBooking(null)}
        booking={rescheduleBooking}
        api={api}
        venues={[]}
        onDone={onRefresh}
      />
      <BookingDetailsModal
        open={!!detailsBooking}
        onClose={() => setDetailsBooking(null)}
        booking={detailsBooking}
      />
    </Card>
  );
};

/* ============================================================
 * Regenerate-Invoice modal for regular bookings (day pass / meeting room).
 * Patches the underlying organization too if booking has a user_id.
 * ============================================================ */
const RegenerateBookingInvoiceDialog = ({ open, onClose, booking, api, onDone }) => {
  const [companyName, setCompanyName] = useState('');
  const [gstin, setGstin] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [updateOrg, setUpdateOrg] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);

  useEffect(() => {
    if (!open || !booking) return;
    setCompanyName(booking.company_name || booking.billing_company || '');
    setGstin(booking.gstin || booking.billing_gstin || '');
    setCompanyAddress(booking.company_address || '');
    setUpdateOrg(true);
    setOrgInfo(null);
  }, [open, booking]);

  const submit = async () => {
    if (!companyName.trim()) { toast.error('Company name is required'); return; }
    if (gstin && gstin.trim().length !== 15) { toast.error('GSTIN must be exactly 15 characters'); return; }
    setSubmitting(true);
    try {
      const res = await api.post(`/bookings/admin/regenerate-invoice/${booking.id}`, {
        company_name: companyName.trim(),
        gstin: gstin.trim() || null,
        company_address: companyAddress.trim() || null,
        update_org: updateOrg,
      });
      const msg = `Invoice regenerated: ${res.data.new_invoice_number}. Old invoice voided in Zoho.${res.data.org_updated ? ' Organization GSTIN also patched — future bookings will use the corrected info.' : ''}`;
      toast.success(msg);
      onDone && onDone();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to regenerate invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="booking-regen-dialog">
        <DialogHeader><DialogTitle>Fix Invoice — Regenerate with Corrected Company + GSTIN</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-2.5 rounded bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-xs text-blue-900 dark:text-blue-200">
            This will <strong>void the existing Zoho invoice</strong> and issue a fresh one. If &quot;Also update organization&quot; is checked and this booking has a linked org, that org record is patched too — so every future booking by this client uses the correct GSTIN.
          </div>
          {booking?.zoho_invoice_number && (
            <div className="text-xs text-muted-foreground">
              Current invoice: <span className="font-mono font-medium">{booking.zoho_invoice_number}</span>
            </div>
          )}
          <div>
            <Label>Company Name *</Label>
            <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Prismforce Private Limited" data-testid="booking-regen-company" />
          </div>
          <div>
            <Label>GSTIN <span className="text-xs text-muted-foreground">(15 chars)</span></Label>
            <Input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())} maxLength={15} placeholder="27AALCP8314J2ZZ" data-testid="booking-regen-gstin" className="font-mono" />
          </div>
          <div>
            <Label>Company Address <span className="text-xs text-muted-foreground">(optional)</span></Label>
            <Textarea value={companyAddress} onChange={(e) => setCompanyAddress(e.target.value)} rows={2} data-testid="booking-regen-address" />
          </div>
          {booking?.user_id && (
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={updateOrg} onChange={(e) => setUpdateOrg(e.target.checked)} data-testid="booking-regen-update-org" />
              <span>Also update the organization record (recommended — fixes future bookings)</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="booking-regen-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Regenerate Invoice
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
